(function productFormControllerFactory(root) {
  function normalizeOptions(options) {
    if (!Array.isArray(options)) {
      return [];
    }

    return options.map((option) => String(option ?? '').trim());
  }

  function findVariantByOptions(variants, selectedOptions, optionNames) {
    const optionValues = optionNames.map((name) => String(selectedOptions[name] ?? '').trim());

    return variants.find((variant) => {
      const variantOptions = normalizeOptions(variant.options);

      return optionValues.every((value, index) => variantOptions[index] === value);
    }) || null;
  }

  function getVariantQuantityRule(variant) {
    const rule = variant && typeof variant.quantity_rule === 'object' && variant.quantity_rule
      ? variant.quantity_rule
      : {};
    const min = Math.max(Number(rule.min) || 1, 1);
    const increment = Math.max(Number(rule.increment) || 1, 1);
    const max = rule.max === null || rule.max === undefined || rule.max === ''
      ? null
      : Math.max(Number(rule.max) || 0, 0);

    return {
      min,
      max,
      increment,
    };
  }

  function alignQuantityToRule(quantity, rule, max) {
    const numericQuantity = Number(quantity);
    const requestedQuantity = Number.isFinite(numericQuantity) && numericQuantity > 0
      ? numericQuantity
      : rule.min;
    const upperBound = max === null ? requestedQuantity : max;
    const clampedQuantity = Math.max(rule.min, Math.min(requestedQuantity, upperBound));
    const steps = Math.floor((clampedQuantity - rule.min) / rule.increment);

    return rule.min + (steps * rule.increment);
  }

  function getVariantQuantityState(variant, requestedQuantity, cartQuantity = 0) {
    const available = Boolean(variant && variant.available);
    const inventoryManagement = variant ? variant.inventory_management : null;
    const inventoryPolicy = variant ? variant.inventory_policy : null;
    const inventoryQuantity = Number(variant ? variant.inventory_quantity : 0);
    const quantityInCart = Math.max(Number(cartQuantity) || 0, 0);
    const tracksInventory = inventoryManagement === 'shopify' && inventoryPolicy !== 'continue';
    const rule = getVariantQuantityRule(variant);

    if (!available) {
      return {
        value: 1,
        min: 1,
        max: 1,
        increment: 1,
        disabled: true,
      };
    }

    if (tracksInventory) {
      const inventoryMax = Math.max(0, inventoryQuantity - quantityInCart);
      const rawMax = rule.max === null ? inventoryMax : Math.min(rule.max, inventoryMax);

      if (rawMax < rule.min) {
        return {
          value: 0,
          min: 0,
          max: 0,
          increment: rule.increment,
          disabled: true,
        };
      }

      const max = alignQuantityToRule(rawMax, rule, rawMax);

      return {
        value: alignQuantityToRule(requestedQuantity, rule, max),
        min: rule.min,
        max,
        increment: rule.increment,
        disabled: false,
      };
    }

    return {
      value: alignQuantityToRule(requestedQuantity, rule, rule.max),
      min: rule.min,
      max: rule.max === null ? null : alignQuantityToRule(rule.max, rule, rule.max),
      increment: rule.increment,
      disabled: false,
    };
  }

  function getVariantStockCount(variant, stockScaleMax, cartQuantity = 0) {
    if (!variant || !variant.available) {
      return 0;
    }

    const inventoryManagement = variant.inventory_management;
    const inventoryPolicy = variant.inventory_policy;
    const tracksInventory = inventoryManagement === 'shopify' && inventoryPolicy !== 'continue';
    const inventoryQuantity = Math.max(
      Number(variant.inventory_quantity || 0) - Math.max(Number(cartQuantity) || 0, 0),
      0,
    );

    if (tracksInventory) {
      return inventoryQuantity;
    }

    return Math.max(Number(stockScaleMax) || 0, 0);
  }

  const STOCK_ALERT_THRESHOLD = 5;

  function shouldShowStockAlert(variant, stockCount) {
    if (!variant || !variant.available) {
      return false;
    }

    if (variant.inventory_management !== 'shopify' || variant.inventory_policy === 'continue') {
      return false;
    }

    const count = Math.max(Number(stockCount) || 0, 0);
    return count > 0 && count < STOCK_ALERT_THRESHOLD;
  }

  function getStockTone() {
    return 'red';
  }

  function buildCartAddPayload(variantId, quantity) {
    return {
      id: Number(variantId),
      quantity: Math.max(Number(quantity) || 1, 1),
    };
  }

  function buildCartAddItemsPayload(items) {
    const payloadItems = (Array.isArray(items) ? items : [])
      .map((item) => buildCartAddPayload(item.id, item.quantity))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);

    return {
      items: payloadItems,
    };
  }

  function buildCartApiPath(path) {
    const routeRoot = root.Shopify?.routes?.root || '/';
    const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const normalizedPath = String(path || '').replace(/^\//, '');

    return `${normalizedRoot}${normalizedPath}`;
  }

  function parseCartError(error) {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    return error.description || error.message || '';
  }

  function updateHeaderCartCount(cart, template = root.theme?.cartCountTemplate || '') {
    if (!root.document || !cart || typeof cart.item_count === 'undefined') {
      return;
    }

    const count = String(cart.item_count);
    const label = String(template || '').replace('__count__', count);

    root.document.querySelectorAll('.site-header__cart-count').forEach((countBadge) => {
      countBadge.textContent = count;
      if (label) {
        countBadge.setAttribute('aria-label', label);
      }
    });
  }

  function dispatchCartUpdated(cart, detail = {}) {
    if (
      !root.document
      || !cart
      || typeof cart.item_count === 'undefined'
      || typeof root.document.dispatchEvent !== 'function'
      || typeof root.CustomEvent !== 'function'
    ) {
      return;
    }

    root.document.dispatchEvent(new root.CustomEvent('cart:updated', {
      detail: {
        cart,
        source: 'product-page',
        ...detail,
      },
    }));
  }

  function dispatchCartError(error, fallbackMessage = '') {
    if (!root.document || typeof root.document.dispatchEvent !== 'function' || typeof root.CustomEvent !== 'function') {
      return;
    }

    root.document.dispatchEvent(new root.CustomEvent('cart:error', {
      detail: {
        cart: error?.cart || null,
        message: parseCartError(error) || fallbackMessage,
      },
    }));
  }

  async function requestCartJson(path, options) {
    if (!root.fetch) {
      return {};
    }

    const response = await root.fetch(buildCartApiPath(path), options || {});
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(parseCartError(data) || response.statusText);
    }

    return data;
  }

  function buildVariantChangeDetail(variant) {
    return {
      id: variant && variant.id ? variant.id : '',
      sku: variant && variant.sku ? String(variant.sku).trim() : '',
      mediaId: variant && variant.featured_media_id ? variant.featured_media_id : null,
    };
  }

  function formatMoney(cents) {
    if (root.Shopify && typeof root.Shopify.formatMoney === 'function') {
      return root.Shopify.formatMoney(cents);
    }

    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  function randomizeBundleKeys(keys, limit, shouldRandomize, random = Math.random) {
    const uniqueKeys = Array.from(new Set((Array.isArray(keys) ? keys : []).filter(Boolean)));
    const itemLimit = Math.max(Number(limit) || uniqueKeys.length, 0);

    if (shouldRandomize) {
      for (let index = uniqueKeys.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(random() * (index + 1));
        [uniqueKeys[index], uniqueKeys[randomIndex]] = [uniqueKeys[randomIndex], uniqueKeys[index]];
      }
    }

    return uniqueKeys.slice(0, itemLimit);
  }

  function selectBundleKeysBySlot(entries, limit, shouldRandomize, random = Math.random) {
    const slotGroups = new Map();

    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      const key = String(entry?.key || '');
      const slot = String(entry?.slot || '');
      const variantId = String(entry?.variantId || '');

      if (!key || !slot || !variantId) {
        return;
      }

      if (!slotGroups.has(slot)) {
        slotGroups.set(slot, []);
      }

      const candidates = slotGroups.get(slot);
      if (!candidates.some((candidate) => candidate.key === key)) {
        candidates.push({ key, variantId });
      }
    });

    const slotLimit = Math.max(Number(limit) || slotGroups.size, 0);
    const candidateGroups = Array.from(slotGroups.values()).slice(0, slotLimit);

    candidateGroups.forEach((candidates) => {
      if (shouldRandomize) {
        for (let index = candidates.length - 1; index > 0; index -= 1) {
          const randomIndex = Math.floor(random() * (index + 1));
          [candidates[index], candidates[randomIndex]] = [
            candidates[randomIndex],
            candidates[index],
          ];
        }
      }
    });

    function findUniqueSelection(groupIndex, selectedVariantIds, selectedKeys) {
      if (groupIndex >= candidateGroups.length) {
        return selectedKeys;
      }

      for (const candidate of candidateGroups[groupIndex]) {
        if (selectedVariantIds.has(candidate.variantId)) {
          continue;
        }

        const nextVariantIds = new Set(selectedVariantIds);
        nextVariantIds.add(candidate.variantId);
        const result = findUniqueSelection(
          groupIndex + 1,
          nextVariantIds,
          [...selectedKeys, candidate.key],
        );

        if (result) {
          return result;
        }
      }

      return null;
    }

    return findUniqueSelection(0, new Set(), []) || [];
  }

  function createBundleGroupKey() {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const randomValues = new Uint32Array(12);

    if (root.crypto && typeof root.crypto.getRandomValues === 'function') {
      root.crypto.getRandomValues(randomValues);
    } else {
      randomValues.forEach((_value, index) => {
        randomValues[index] = Math.floor(Math.random() * alphabet.length);
      });
    }

    return Array.from(randomValues)
      .map((value) => alphabet[value % alphabet.length])
      .join('');
  }

  function buildBundleLineProperties(config, groupKey, options = {}) {
    const bundleData = {
      offerId: String(config?.offerId || ''),
      groupKey: String(groupKey || ''),
    };

    if (options.giftFromId) {
      bundleData.giftFromId = String(options.giftFromId);
    }

    if (options.isTrigger) {
      bundleData.isTrigger = true;
    }

    if (options.isGift) {
      bundleData.isGift = true;
    }

    bundleData.minQty = Math.max(Number(config?.minimumItems) || 1, 1);

    return {
      _aov_bundles: JSON.stringify(bundleData),
    };
  }

  function parseBundleLineData(properties) {
    const rawBundleData = properties?._aov_bundles;

    if (!rawBundleData) {
      return {};
    }

    if (typeof rawBundleData === 'object') {
      return rawBundleData;
    }

    try {
      return JSON.parse(rawBundleData);
    } catch (_error) {
      return {};
    }
  }

  function cartHasBundleGift(cart, stickerProductId) {
    return (Array.isArray(cart?.items) ? cart.items : []).some((item) => {
      if (stickerProductId && Number(item?.product_id) === Number(stickerProductId)) {
        return true;
      }

      return parseBundleLineData(item?.properties).isGift === true;
    });
  }

  function mergeDiscountCodes(cart, discountCode) {
    const codes = (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
      .map((discount) => String(discount?.code || '').trim())
      .filter(Boolean);
    const normalizedCode = String(discountCode || '').trim();

    if (normalizedCode) {
      codes.push(normalizedCode);
    }

    return Array.from(new Set(codes)).join(',');
  }

  function removeStaleBundleDiscountCodes(cart, managedDiscountCodes = []) {
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    const hasBundleLines = cartItems.some((item) => Boolean(item?.properties?._aov_bundles));
    if (hasBundleLines) {
      return null;
    }

    const managedCodes = new Set(
      (Array.isArray(managedDiscountCodes) ? managedDiscountCodes : [])
        .map((code) => String(code || '').trim())
        .filter(Boolean),
    );
    if (!managedCodes.size) {
      return null;
    }

    const currentCodes = (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
      .map((discount) => String(discount?.code || '').trim())
      .filter(Boolean);
    const nextCodes = currentCodes.filter((code) => !managedCodes.has(code));

    return nextCodes.length === currentCodes.length ? null : nextCodes.join(',');
  }

  function registerAlpine() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('productGallery', (config = {}) => ({
      activeIndex: 0,
      total: Number(config.total ?? 1),
      mediaIds: (Array.isArray(config.mediaIds) ? config.mediaIds : []).map((id) => String(id)),
      featuredMediaId: config.featuredMediaId == null ? '' : String(config.featuredMediaId),
      touchStartX: null,
      touchEndX: null,
      swipeThreshold: 40,
      swiped: false,

      get canNavigate() {
        return this.total > 1;
      },

      indexOfMedia(mediaId) {
        if (mediaId == null || mediaId === '') {
          return -1;
        }

        return this.mediaIds.indexOf(String(mediaId));
      },

      onVariantChange(detail) {
        if (!this.mediaIds.length) {
          return;
        }

        let index = this.indexOfMedia(detail && detail.mediaId);

        if (index < 0) {
          index = this.indexOfMedia(this.featuredMediaId);
        }

        if (index >= 0) {
          this.setActive(index);
        }
      },

      setActive(index) {
        this.activeIndex = Math.max(0, Math.min(this.total - 1, Number(index)));
        this.syncThumbs();
      },

      next() {
        if (!this.canNavigate) {
          return;
        }

        this.setActive((this.activeIndex + 1) % this.total);
      },

      prev() {
        if (!this.canNavigate) {
          return;
        }

        this.setActive((this.activeIndex - 1 + this.total) % this.total);
      },

      expandImage(event) {
        this.openLightbox(event?.currentTarget?.parentElement, event?.currentTarget);
      },

      // Clicking the photo itself opens the viewer; controls, videos and the
      // tail end of a swipe are left alone.
      onMainClick(event) {
        if (this.swiped) {
          this.swiped = false;
          return;
        }

        const target = event?.target;
        if (!target || target.closest?.('button, a, video, iframe, model-viewer')) {
          return;
        }

        if (!target.closest?.('[data-product-gallery-slide]')) {
          return;
        }

        this.openLightbox(event.currentTarget, target);
      },

      // Hands the viewer every photo in the gallery at full resolution, so it
      // can page between them and zoom past the cropped on-page size.
      openLightbox(galleryMain, trigger) {
        const slides = Array.from(galleryMain?.querySelectorAll?.('[data-product-gallery-slide]') || []);
        if (!slides.length || !root.imageLightbox) {
          return;
        }

        const items = [];
        let startIndex = 0;

        slides.forEach((slide, slideIndex) => {
          const image = slide.querySelector?.('img');
          if (!image || slide.querySelector?.('video, iframe, model-viewer')) {
            return;
          }

          if (slideIndex === this.activeIndex) {
            startIndex = items.length;
          }

          items.push({
            src: slide.dataset?.fullSrc || image.currentSrc || image.src,
            alt: image.alt || '',
          });
        });

        if (!items.length) {
          return;
        }

        root.imageLightbox.open({
          items,
          index: startIndex,
          trigger: trigger && typeof trigger.focus === 'function' ? trigger : null,
        });
      },

      onTouchStart(event) {
        const touch = event.touches?.[0];
        this.touchStartX = touch ? touch.clientX : null;
        this.touchEndX = null;
        this.swiped = false;
      },

      onTouchEnd(event) {
        const touch = event.changedTouches?.[0];
        this.touchEndX = touch ? touch.clientX : null;
        this.handleSwipe();
      },

      onDragStart(event) {
        this.touchStartX = event.clientX;
        this.touchEndX = null;
        this.swiped = false;
      },

      onDragEnd(event) {
        this.touchEndX = event.clientX;
        this.handleSwipe();
      },

      onDragCancel() {
        this.touchStartX = null;
        this.touchEndX = null;
      },

      handleSwipe() {
        if (!this.canNavigate || this.touchStartX === null || this.touchEndX === null) {
          return;
        }

        const deltaX = this.touchStartX - this.touchEndX;
        if (Math.abs(deltaX) < this.swipeThreshold) {
          return;
        }

        if (deltaX > 0) {
          this.next();
        } else {
          this.prev();
        }

        this.swiped = true;
        this.touchStartX = null;
        this.touchEndX = null;
      },

      syncThumbs() {
        const track = this.$refs.thumbs;
        const activeThumb = track?.children?.[this.activeIndex];
        if (!activeThumb || typeof activeThumb.scrollIntoView !== 'function') {
          return;
        }

        const reduceMotion = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
        activeThumb.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      },
    }));

    root.Alpine.data('productPage', (config = {}) => ({
      sourceId: config.sourceId || '',
      variants: Array.isArray(config.variants) ? config.variants : [],
      optionNames: Array.isArray(config.optionNames) ? config.optionNames : [],
      selectedOptions: { ...(config.selectedOptions || {}) },
      addToCartText: config.addToCartText || 'Add to cart',
      soldOutText: config.soldOutText || 'Sold out',
      saveAmountText: config.saveAmountText || 'Save {amount}',
      inStockText: config.inStockText || 'In stock',
      outOfStockText: config.outOfStockText || 'Out of stock',
      stockLeftText: config.stockLeftText || 'Only {count} left in stock. Order soon!',
      stockCountText: config.stockCountText || '{count} in stock',
      quantity: Number(config.quantity ?? 1),
      stockScaleMax: Number(config.stockScaleMax ?? 20),
      stockAlertThreshold: Number(config.stockAlertThreshold ?? STOCK_ALERT_THRESHOLD),
      rating: Number(config.rating ?? 0) || 0,
      ratingCount: Number(config.ratingCount ?? 0) || 0,
      ratingScaleMax: Math.max(Number(config.ratingScaleMax ?? 5) || 5, 1),
      reviewCountTemplate: config.reviewCountTemplate || '{{ count }} ratings',
      reviewCountOneTemplate: config.reviewCountOneTemplate || '',
      ratingLabelTemplate: config.ratingLabelTemplate || '{{ rating }} out of 5 stars',
      stockAnimating: false,
      adding: false,
      cart: { items: [] },
      cartStatusMessage: '',
      cartStatusIsError: false,
      addedToCartText: config.addedToCartText || '',
      maxInCartText: config.maxInCartText || '',
      cartErrorText: config.cartErrorText || '',
      bundleConfig: {
        // Keep the bundle presentation available while its cart and discount workflow is paused.
        enabled: false,
        offerId: '',
        discountCode: '',
        managedDiscountCodes: [],
        discountPercent: 10,
        itemLimit: 0,
        minimumItems: 1,
        randomize: false,
        stickerProductId: '',
        stickerVariantId: '',
        stickerAvailable: false,
        addLabelTemplate: '',
        totalLabelTemplate: '',
        ...(config.bundleConfig || {}),
      },
      bundleOpen: true,
      visibleBundleKeys: [],
      selectedBundleKeys: [],

      init() {
        this.loadSourceData();
        this.syncQuantity();
        this.notifyVariantChange();
        // Nested components (the gallery) register their window listeners after
        // this parent initializes, so repeat the notification once they exist.
        this.$nextTick(() => this.notifyVariantChange());
        this.refreshCartAvailability();
        this.$el.addEventListener('product-reviews:updated', (event) => {
          this.syncProductRating(event.detail || {});
        });
        this.$nextTick(() => this.initializeBundle());
      },

      loadSourceData() {
        if (!this.sourceId || !root.document) {
          return;
        }

        const source = root.document.getElementById(this.sourceId);
        if (!source) {
          return;
        }

        try {
          const data = JSON.parse(source.textContent || '{}');
          this.variants = Array.isArray(data.variants) ? data.variants : this.variants;
          this.optionNames = Array.isArray(data.optionNames) ? data.optionNames : this.optionNames;
          this.selectedOptions = data.selectedOptions || this.selectedOptions;
          this.quantity = Number(data.quantity ?? this.quantity);
          this.stockScaleMax = Number(data.stockScaleMax ?? this.stockScaleMax);
          this.stockAlertThreshold = Number(data.stockAlertThreshold ?? this.stockAlertThreshold);
          this.rating = Number(data.rating ?? this.rating) || 0;
          this.ratingCount = Number(data.ratingCount ?? this.ratingCount) || 0;
          this.ratingScaleMax = Math.max(Number(data.ratingScaleMax ?? this.ratingScaleMax) || 5, 1);
          this.reviewCountTemplate = data.reviewCountTemplate || this.reviewCountTemplate;
          this.reviewCountOneTemplate = data.reviewCountOneTemplate || this.reviewCountOneTemplate;
          this.ratingLabelTemplate = data.ratingLabelTemplate || this.ratingLabelTemplate;
          this.addToCartText = data.addToCartText || this.addToCartText;
          this.soldOutText = data.soldOutText || this.soldOutText;
          this.saveAmountText = data.saveAmountText || this.saveAmountText;
          this.inStockText = data.inStockText || this.inStockText;
          this.outOfStockText = data.outOfStockText || this.outOfStockText;
          this.stockLeftText = data.stockLeftText || this.stockLeftText;
          this.stockCountText = data.stockCountText || this.stockCountText;
          this.addedToCartText = data.addedToCartText || this.addedToCartText;
          this.maxInCartText = data.maxInCartText || this.maxInCartText;
          this.cartErrorText = data.cartErrorText || this.cartErrorText;
          this.bundleConfig = {
            ...this.bundleConfig,
            ...(data.bundleConfig || {}),
          };
        } catch (error) {
          this.variants = this.variants;
        }
      },

      initializeBundle() {
        const cards = Array.from(this.$root?.querySelectorAll?.('[data-bundle-card]') || []);
        const slottedCards = cards
          .map((card) => ({
            key: card.dataset.bundleKey,
            slot: card.dataset.bundleSlot,
            variantId: card.dataset.bundleVariantId,
          }))
          .filter((entry) => entry.key && entry.slot && entry.variantId);

        if (slottedCards.length > 0) {
          this.visibleBundleKeys = selectBundleKeysBySlot(
            slottedCards,
            this.bundleConfig.itemLimit,
            Boolean(this.bundleConfig.randomize),
          );
        } else {
          const keys = cards
            .map((card) => card.dataset.bundleKey)
            .filter(Boolean);

          this.visibleBundleKeys = randomizeBundleKeys(
            keys,
            this.bundleConfig.itemLimit,
            Boolean(this.bundleConfig.randomize),
          );
        }
        this.selectedBundleKeys = [...this.visibleBundleKeys];
      },

      syncProductRating(detail = {}) {
        if (!detail || detail.source !== 'yotpo') {
          return;
        }

        const nextCount = Number(detail.ratingCount);
        const nextRating = Number(detail.rating);

        if (!Number.isFinite(nextCount) || nextCount < 0) {
          return;
        }

        this.ratingCount = nextCount;
        this.rating = nextCount > 0 && Number.isFinite(nextRating) && nextRating > 0
          ? nextRating
          : 0;
      },

      get showProductRating() {
        return this.ratingCount > 0 && Number(this.rating) > 1;
      },

      get productRatingCountLabel() {
        const template = Number(this.ratingCount) === 1 && this.reviewCountOneTemplate
          ? this.reviewCountOneTemplate
          : this.reviewCountTemplate;

        return String(template || '{{ count }} ratings')
          .replace('__COUNT__', String(this.ratingCount))
          .replace('{{ count }}', String(this.ratingCount));
      },

      get productRatingLabel() {
        const ratingDisplay = Number(this.rating || 0).toFixed(1);

        return String(this.ratingLabelTemplate || '{{ rating }} out of 5 stars')
          .replace('__RATING__', ratingDisplay)
          .replace('{{ rating }}', ratingDisplay);
      },

      get selectedVariant() {
        return findVariantByOptions(this.variants, this.selectedOptions, this.optionNames) || this.variants[0] || null;
      },

      get selectedVariantId() {
        return this.selectedVariant ? this.selectedVariant.id : '';
      },

      get selectedSku() {
        return buildVariantChangeDetail(this.selectedVariant).sku;
      },

      formatMoney(cents) {
        return formatMoney(cents);
      },

      get isAvailable() {
        return Boolean(this.selectedVariant && this.selectedVariant.available);
      },

      get isProductSoldOut() {
        return this.variants.length > 0 && !this.variants.some((variant) => variant && variant.available);
      },

      get price() {
        return this.selectedVariant ? Number(this.selectedVariant.price || 0) : 0;
      },

      get compareAtPrice() {
        return this.selectedVariant ? Number(this.selectedVariant.compare_at_price || 0) : 0;
      },

      get showCompareAtPrice() {
        return this.compareAtPrice > this.price;
      },

      get savingsAmount() {
        return Math.max(this.compareAtPrice - this.price, 0);
      },

      get savingsLabel() {
        return this.saveAmountText.replace('{amount}', formatMoney(this.savingsAmount));
      },

      get quantityState() {
        return getVariantQuantityState(this.selectedVariant, this.quantity, this.cartQuantity);
      },

      get cartQuantity() {
        if (!root.cartInventory || !this.selectedVariant) {
          return 0;
        }

        return root.cartInventory.getCartVariantQuantity(this.cart, this.selectedVariant.id);
      },

      get quantityMin() {
        return this.quantityState.min;
      },

      get quantityMax() {
        return this.quantityState.max;
      },

      get quantityIncrement() {
        return this.quantityState.increment;
      },

      get quantityDisabled() {
        return this.quantityState.disabled;
      },

      get stockCount() {
        return getVariantStockCount(this.selectedVariant, this.stockScaleMax, this.cartQuantity);
      },

      get showStockAlert() {
        return shouldShowStockAlert(this.selectedVariant, this.stockCount);
      },

      get stockPercent() {
        if (!this.showStockAlert) {
          return 0;
        }

        return Math.min(Math.round((this.stockCount * 100) / this.stockScaleMax), 100);
      },

      get stockTone() {
        return getStockTone();
      },

      get stockIcon() {
        return 'ph-warning';
      },

      get stockMessage() {
        if (!this.showStockAlert) {
          return '';
        }

        return this.stockLeftText.replace('{count}', this.stockCount);
      },

      get stockCountLabel() {
        return this.stockCountText.replace('{count}', this.stockCount);
      },

      get addToCartLabel() {
        if (this.isAvailable && this.quantityDisabled) {
          return this.maxInCartText;
        }

        return `${this.addToCartText} ${formatMoney(this.price)}`;
      },

      get selectedBundleItems() {
        const selectedKeys = new Set(this.selectedBundleKeys);

        return Array.from(this.$root?.querySelectorAll?.('[data-bundle-card]') || [])
          .filter((card) => selectedKeys.has(card.dataset.bundleKey))
          .map((card) => ({
            id: card.dataset.bundleVariantId,
            quantity: Math.max(Number(card.dataset.bundleQuantity) || 1, 1),
            maxQuantity: card.dataset.bundleQuantityMax || null,
            minimum: Math.max(Number(card.dataset.bundleQuantityMin) || 1, 1),
            increment: Math.max(Number(card.dataset.bundleQuantityIncrement) || 1, 1),
            price: Number(card.dataset.bundlePrice || 0),
          }));
      },

      get bundleMainQuantity() {
        return getVariantQuantityRule(this.selectedVariant).min;
      },

      get bundleOriginalTotal() {
        return (this.price * this.bundleMainQuantity) + this.selectedBundleItems.reduce(
          (total, item) => total + (Number(item.price || 0) * Number(item.quantity || 1)),
          0,
        );
      },

      get bundleDiscountedTotal() {
        const multiplier = 100 - Math.max(
          Math.min(Number(this.bundleConfig.discountPercent) || 0, 100),
          0,
        );
        return Math.round((this.bundleOriginalTotal * multiplier) / 100);
      },

      get bundleComplete() {
        const selectedItems = this.selectedBundleItems;
        const itemLimit = Number(this.bundleConfig.itemLimit);
        const distinctVariantIds = new Set(selectedItems.map((item) => String(item.id)));

        return selectedItems.length === itemLimit
          && distinctVariantIds.size === itemLimit
          && selectedItems.every((item) => item.quantity >= item.minimum);
      },

      get bundleTotalQuantity() {
        return this.bundleMainQuantity + this.selectedBundleItems.reduce(
          (total, item) => total + Number(item.quantity || 0),
          0,
        );
      },

      get bundleItemCountLabel() {
        return String(this.bundleConfig.totalLabelTemplate || '')
          .replace('{count}', this.bundleTotalQuantity);
      },

      get bundleCtaLabel() {
        return String(this.bundleConfig.addLabelTemplate || '')
          .replace('{price}', formatMoney(this.bundleDiscountedTotal));
      },

      isBundleCardVisible(key) {
        return this.visibleBundleKeys.includes(String(key || ''));
      },

      isBundleItemSelected(key) {
        return this.selectedBundleKeys.includes(String(key || ''));
      },

      toggleBundleItem(key) {
        const normalizedKey = String(key || '');
        if (!this.visibleBundleKeys.includes(normalizedKey)) {
          return;
        }

        if (this.selectedBundleKeys.includes(normalizedKey)) {
          this.selectedBundleKeys = this.selectedBundleKeys.filter((itemKey) => itemKey !== normalizedKey);
        } else {
          this.selectedBundleKeys = [...this.selectedBundleKeys, normalizedKey];
        }
      },

      selectOption(name, value) {
        this.selectedOptions[name] = value;
        this.cartStatusMessage = '';
        this.syncQuantity();
        this.updateUrl();
        this.notifyVariantChange();
      },

      syncQuantity() {
        this.quantity = this.quantityState.value;
        this.restartStockAnimation();
      },

      syncCartAvailability(cart) {
        if (!cart) {
          return;
        }

        this.cart = cart;
        this.syncQuantity();
      },

      handleCartUpdated(event) {
        const detail = event?.detail || {};
        this.syncCartAvailability(detail.cart);

        if (detail.source === 'product-page') {
          return;
        }

        this.cartStatusMessage = '';
        this.cartStatusIsError = false;
      },

      async refreshCartAvailability() {
        if (!root.cartInventory) {
          return;
        }

        try {
          this.syncCartAvailability(await root.cartInventory.fetchCart());
        } catch (_error) {
          // The add request repeats this inventory check immediately before mutation.
        }
      },

      restartStockAnimation() {
        this.stockAnimating = false;
        this.$nextTick(() => {
          this.stockAnimating = this.showStockAlert;
        });
      },

      notifyVariantChange() {
        if (!root.dispatchEvent || typeof root.CustomEvent !== 'function') {
          return;
        }

        root.dispatchEvent(new root.CustomEvent('product:variant-change', {
          detail: buildVariantChangeDetail(this.selectedVariant),
        }));
      },

      incrementQuantity() {
        const nextValue = this.quantity + this.quantityIncrement;
        const max = this.quantityMax;
        this.quantity = max === null ? nextValue : Math.min(nextValue, max);
        this.syncQuantity();
      },

      decrementQuantity() {
        this.quantity = Math.max(this.quantity - this.quantityIncrement, this.quantityMin);
        this.syncQuantity();
      },

      updateUrl() {
        if (!this.selectedVariantId || !root.history || !root.URL || !root.location) {
          return;
        }

        const url = new URL(root.location.href);
        url.searchParams.set('variant', this.selectedVariantId);
        root.history.replaceState({}, '', url.toString());
      },

      async addToCart() {
        if (this.adding || this.quantityDisabled || !this.selectedVariant || !root.cartInventory) {
          return;
        }

        this.adding = true;
        this.cartStatusMessage = '';
        this.cartStatusIsError = false;

        try {
          const absoluteQuantityState = getVariantQuantityState(this.selectedVariant, this.quantity, 0);
          const result = await root.cartInventory.addVariant({
            id: this.selectedVariant.id,
            quantity: this.quantityState.value,
            maxQuantity: absoluteQuantityState.max,
            minimum: absoluteQuantityState.min,
            increment: absoluteQuantityState.increment,
          }, {
            unavailableMessage: this.maxInCartText,
            finalize: async (addResult) => {
              const nextDiscountCodes = removeStaleBundleDiscountCodes(
                addResult.cart,
                this.bundleConfig.managedDiscountCodes,
              );
              if (nextDiscountCodes === null) {
                return addResult.cart;
              }

              return requestCartJson('/cart/update.js?app=aov-ai-bundle', {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ discount: nextDiscountCodes }),
              });
            },
          });

          this.syncCartAvailability(result.cart);
          updateHeaderCartCount(result.cart);
          this.cartStatusMessage = result.message
            || (result.addedItems.length ? this.addedToCartText : this.maxInCartText);
          this.cartStatusIsError = Boolean(result.message);
          dispatchCartUpdated(result.cart, { message: result.message || '' });
        } catch (error) {
          this.cartStatusMessage = parseCartError(error) || this.cartErrorText;
          this.cartStatusIsError = true;
          if (error?.cart) {
            this.syncCartAvailability(error.cart);
            updateHeaderCartCount(error.cart);
            dispatchCartUpdated(error.cart, { openDrawer: false });
          } else {
            await this.refreshCartAvailability();
          }
          dispatchCartError(error, this.cartErrorText);
        } finally {
          this.adding = false;
        }
      },

      async addComplementaryItem(event) {
        const button = event.currentTarget;
        const variantId = button?.dataset?.complementaryVariantId;
        if (!variantId || !root.cartInventory) {
          return;
        }

        button.disabled = true;

        try {
          const result = await root.cartInventory.addVariant({
            id: variantId,
            quantity: button?.dataset?.complementaryQuantity || 1,
            maxQuantity: button?.dataset?.complementaryQuantityMax || null,
            minimum: button?.dataset?.complementaryQuantityMin || 1,
            increment: button?.dataset?.complementaryQuantityIncrement || 1,
          }, {
            unavailableMessage: this.maxInCartText,
          });

          this.syncCartAvailability(result.cart);
          updateHeaderCartCount(result.cart);
          this.cartStatusMessage = result.message
            || (result.addedItems.length ? this.addedToCartText : this.maxInCartText);
          this.cartStatusIsError = Boolean(result.message);
          dispatchCartUpdated(result.cart, { message: result.message || '' });

          // Confirm on the button itself: the cart drawer may be closed, and a
          // silent success reads as a dead button.
          if (result.addedItems.length && !result.message) {
            button.classList.add('is-added');
            root.setTimeout(() => button.classList.remove('is-added'), 2000);
          }
        } catch (error) {
          if (error?.cart) {
            this.syncCartAvailability(error.cart);
            updateHeaderCartCount(error.cart);
          }
          this.cartStatusMessage = parseCartError(error) || this.cartErrorText;
          this.cartStatusIsError = true;
          dispatchCartError(error, this.cartErrorText);
        } finally {
          button.disabled = false;
        }
      },

      async addBundleItem(event) {
        const button = event.currentTarget;
        const variantId = button?.dataset?.bundleVariantId;
        const quantity = button?.dataset?.bundleQuantity || 1;
        if (!this.bundleConfig.enabled || !variantId || !root.cartInventory) {
          return;
        }

        button.disabled = true;

        try {
          const result = await root.cartInventory.addVariant({
            id: variantId,
            quantity,
            maxQuantity: button?.dataset?.bundleQuantityMax || null,
            minimum: button?.dataset?.bundleQuantityMin || 1,
            increment: button?.dataset?.bundleQuantityIncrement || 1,
          });
          this.syncCartAvailability(result.cart);
          updateHeaderCartCount(result.cart);
          this.cartStatusMessage = result.message
            || (result.addedItems.length ? this.addedToCartText : this.maxInCartText);
          this.cartStatusIsError = Boolean(result.message);
          dispatchCartUpdated(result.cart, { message: result.message || '' });
        } catch (error) {
          if (error?.cart) {
            this.syncCartAvailability(error.cart);
            updateHeaderCartCount(error.cart);
          }
          this.cartStatusMessage = parseCartError(error) || this.cartErrorText;
          this.cartStatusIsError = true;
          dispatchCartError(error, this.cartErrorText);
        } finally {
          button.disabled = false;
        }
      },

      async addBundleItems(event) {
        const button = event.currentTarget;
        if (
          !this.bundleConfig.enabled
          || !this.bundleComplete
          || this.quantityDisabled
          || !this.selectedVariant
          || !root.cartInventory
        ) {
          return;
        }

        this.adding = true;
        button.disabled = true;
        this.cartStatusMessage = '';
        this.cartStatusIsError = false;

        try {
          const groupKey = createBundleGroupKey();
          const mainQuantityState = getVariantQuantityState(
            this.selectedVariant,
            this.bundleMainQuantity,
            0,
          );
          const bundleItems = [
            {
              id: this.selectedVariant.id,
              quantity: mainQuantityState.value,
              maxQuantity: mainQuantityState.max,
              minimum: mainQuantityState.min,
              increment: mainQuantityState.increment,
              properties: buildBundleLineProperties(this.bundleConfig, groupKey, { isTrigger: true }),
            },
            ...this.selectedBundleItems.map((item) => ({
              ...item,
              properties: buildBundleLineProperties(this.bundleConfig, groupKey),
            })),
          ];

          const result = await root.cartInventory.addItemsExact((cartBeforeAdd) => {
            const requestedItems = bundleItems.map((item) => ({
              ...item,
              properties: { ...item.properties },
            }));

            if (
              this.bundleConfig.stickerAvailable
              && this.bundleConfig.stickerVariantId
              && !cartHasBundleGift(cartBeforeAdd, this.bundleConfig.stickerProductId)
            ) {
              requestedItems.push({
                id: this.bundleConfig.stickerVariantId,
                quantity: 1,
                properties: buildBundleLineProperties(this.bundleConfig, groupKey, {
                  giftFromId: this.bundleConfig.productId,
                  isGift: true,
                }),
              });
            }

            return requestedItems;
          }, {
            unavailableMessage: this.maxInCartText,
            finalize: async (addResult) => {
              const discountCode = String(this.bundleConfig.discountCode || '').trim();
              if (!discountCode) {
                return addResult.cart;
              }

              const discountCodes = mergeDiscountCodes(addResult.cart, discountCode);
              return requestCartJson('/cart/update.js?app=aov-ai-bundle', {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ discount: discountCodes }),
              });
            },
          });

          this.syncCartAvailability(result.cart);
          updateHeaderCartCount(result.cart);
          this.cartStatusMessage = result.message || '';
          this.cartStatusIsError = Boolean(result.message);
          dispatchCartUpdated(result.cart, {
            message: result.message || '',
            openDrawer: true,
          });
        } catch (error) {
          if (error?.cart) {
            this.syncCartAvailability(error.cart);
            updateHeaderCartCount(error.cart);
          }
          this.cartStatusMessage = parseCartError(error) || this.cartErrorText;
          this.cartStatusIsError = true;
          dispatchCartError(error, this.cartErrorText);
        } finally {
          this.adding = false;
          button.disabled = false;
        }
      },
    }));
  }

  if (root.document) {
    root.document.addEventListener('alpine:init', registerAlpine);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildCartAddItemsPayload,
      buildCartAddPayload,
      buildCartApiPath,
      buildBundleLineProperties,
      buildVariantChangeDetail,
      cartHasBundleGift,
      createBundleGroupKey,
      findVariantByOptions,
      getStockTone,
      shouldShowStockAlert,
      STOCK_ALERT_THRESHOLD,
      getVariantQuantityRule,
      getVariantStockCount,
      getVariantQuantityState,
      mergeDiscountCodes,
      parseBundleLineData,
      randomizeBundleKeys,
      removeStaleBundleDiscountCodes,
      selectBundleKeysBySlot,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
